
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import NavigationMenu from "@/components/layout/NavigationMenu";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import QuickActions from "@/components/dashboard/QuickActions";
import RecentResultsSection from "@/components/dashboard/RecentResultsSection";
import FavoriteTestsSection from "@/components/dashboard/FavoriteTestsSection";
import ResourcesSection from "@/components/dashboard/ResourcesSection";
import AIAnalysisCard from "@/components/dashboard/AIAnalysisCard";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/seo/SEO";

const Index = () => {
  const [username, setUsername] = useState<string | null>(null);
  const [recentResults, setRecentResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState<Record<string, boolean>>({
    recent: false,
    favorites: false,
    progress: false,
    upcoming: false  // keeping this to avoid breaking any existing code that might reference it
  });
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation(['dashboard', 'common']);

  // Récupérer l'utilisateur et ses données récentes
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        
        // Vérifier l'authentification
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/auth");
          return;
        }
        
        // Récupérer le profil utilisateur
        const { data: profileData } = await supabase
          .from("profiles")
          .select("username")
          .eq("id", user.id)
          .single();
          
        if (profileData) {
          setUsername(profileData.username);
        }
        
        // Récupérer les résultats récents
        const { data: resultsData, error } = await supabase
          .from("level_tests_results")
          .select("*")
          .eq("user_id", user.id)
          .order("test_date", { ascending: false })
          .limit(5);
          
        if (error) {
          throw error;
        }
        
        setRecentResults(resultsData || []);
      } catch (error: any) {
        console.error("Erreur lors de la récupération des données:", error.message);
        toast({
          variant: "destructive",
          title: t('dashboard:errors.loadingError'),
          description: t('dashboard:errors.loadingDescription')
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate, toast]);
  
  // Fonction pour basculer l'état replié/déplié des sections
  const toggleCollapsed = (section: string) => {
    setIsCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <SEO
        title="Golf Progress Calculator — Suivi de progression au golf"
        description="Suivez votre progression au golf sur 7 situations de jeu : putting, wedging, chipping, bunker, fers, bois et driver. 1 mois d'essai offert."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Golf Progress Calculator",
          applicationCategory: "SportsApplication",
          operatingSystem: "Web, iOS, Android",
          offers: { "@type": "Offer", price: "5.00", priceCurrency: "EUR" }
        }}
      />
      <NavigationMenu />
      
      <div className="container mx-auto p-4 md:p-8">
        {/* En-tête */}
        <DashboardHeader username={username} />
        
        {/* Actions rapides */}
        <QuickActions />
        
        {/* Carte d'analyse IA */}
        <div className="mb-8">
          <AIAnalysisCard />
        </div>
        
        {/* Section Résultats Récents */}
        <RecentResultsSection 
          isCollapsed={isCollapsed.recent}
          onToggle={() => toggleCollapsed('recent')}
          recentResults={recentResults}
        />
        
        {/* Section Tests favoris */}
        <FavoriteTestsSection 
          isCollapsed={isCollapsed.favorites}
          onToggle={() => toggleCollapsed('favorites')}
        />
        
        {/* Section Vidéos et ressources */}
        <ResourcesSection 
          isCollapsed={isCollapsed.progress}
          onToggle={() => toggleCollapsed('progress')}
        />
      </div>
      
      <Footer />
    </div>
  );
};

export default Index;
